import { IIssueSummaryResponse } from '@kcftech/prometheustypescriptapiclient';
import { ErrorBoundary } from 'components/error_boundary/ErrorBoundary';
import { memo, useMemo } from 'react';
import { InfoPopup } from 'shared/info_popup/InfoPopup';
import { ErrorFallback } from 'shared/issue_metrics/ErrorFallback';
import { LoadingState } from 'shared/issue_metrics/LoadingState';
import { METRIC_COLORS, MetricIcon } from 'shared/issue_metrics/MetricIcons';
import {
    MetricCard,
    MetricContainer,
    MetricContent,
    MetricIconWrapper,
    MetricLabel,
    MetricSubLabel,
    MetricValue,
} from 'shared/issue_metrics/styled';
import { IssueMetricType } from 'shared/issue_metrics/types';
import { formatMetrics, formatMetricValue } from 'shared/issue_metrics/utils';

type IssueMetricsBaseProps = {
    locationId?: string;
    corporationId?: string;
    groupId?: string;
    metrics: IIssueSummaryResponse | null;
    isLoading: boolean;
    isFetching: boolean;
    error: Error | null;
    metricsConfig?: IssueMetricType[];
};

/**
 * Content component that renders inside the error boundary.
 * This ensures errors are properly caught by the boundary.
 */
export function IssueMetricsContent({
    filteredMetrics,
    level,
    error,
    isLoading,
    isFetching,
    metricCount,
    className,
}: {
    filteredMetrics: ReturnType<typeof formatMetrics> | null;
    level: string;
    error: Error | null;
    isLoading: boolean;
    isFetching: boolean;
    metricCount: number;
    className?: string;
}) {
    if (isLoading || isFetching || !filteredMetrics) {
        return <LoadingState metricCount={metricCount} className={className} />;
    }

    if (error) {
        throw new Error(
            error instanceof Error ? error.message : 'Failed to load metrics'
        );
    }

    return (
        <MetricContainer
            role="region"
            aria-label={`${level} metrics`}
            aria-live="polite"
            tabIndex={0}
            className={className}
        >
            {filteredMetrics.map((metric) => (
                <MetricCard
                    key={metric.type}
                    tabIndex={0}
                    role="article"
                    aria-label={`${metric.label} ${metric.subLabel}: ${formatMetricValue(
                        metric.value,
                        metric.type
                    )}`}
                >
                    <MetricIconWrapper
                        aria-hidden="true"
                        $backgroundColor={METRIC_COLORS[metric.type]?.background}
                        $iconColor={METRIC_COLORS[metric.type]?.icon}
                    >
                        <MetricIcon type={metric.type} />
                    </MetricIconWrapper>
                    <MetricContent>
                        <MetricValue
                            aria-hidden="true"
                            title={formatMetricValue(metric.value, metric.type)}
                        >
                            {formatMetricValue(metric.value, metric.type)}
                        </MetricValue>
                        <MetricLabel>
                            {metric.label}
                            {metric.subLabel && (
                                <MetricSubLabel>{metric.subLabel}</MetricSubLabel>
                            )}
                        </MetricLabel>
                    </MetricContent>
                    {metric.description && (
                        <InfoPopup
                            title={'show more information'}
                            type="info"
                            triggerClassName="info_icon"
                        >
                            {metric.description}
                        </InfoPopup>
                    )}
                </MetricCard>
            ))}
        </MetricContainer>
    );
}

function IssueMetrics({
    locationId,
    corporationId,
    groupId,
    metrics,
    isLoading,
    isFetching,
    error,
    metricsConfig = ['new', 'open', 'days', 'downtime', 'savings'],
}: IssueMetricsBaseProps) {
    const formattedMetrics = useMemo(() => {
        // Only format metrics if we have a valid ID
        if (!(locationId || corporationId || groupId)) {
            return null;
        }
        if (!metrics) {
            return null;
        }
        return formatMetrics(metrics);
    }, [locationId, corporationId, groupId, metrics]);

    // Filter metrics if config is provided
    const filteredMetrics = useMemo(() => {
        if (!formattedMetrics) {
            return null;
        }
        if (metricsConfig.length === 0) {
            return formattedMetrics;
        }
        return formattedMetrics.filter((metric) => metricsConfig.includes(metric.type));
    }, [formattedMetrics, metricsConfig]);

    const level = useMemo(() => {
        if (groupId) {
            return 'Group';
        }
        if (locationId) {
            return 'Location';
        }
        return 'Corporation';
    }, [groupId, locationId]);

    return (
        <ErrorBoundary
            FallbackComponent={ErrorFallback}
            boundaryName="IssueMetricsWidget"
            onReset={() => {
                window.location.reload();
            }}
            resetKeys={[groupId, locationId, corporationId]}
        >
            <IssueMetricsContent
                filteredMetrics={filteredMetrics}
                level={level}
                error={error}
                isLoading={isLoading}
                isFetching={isFetching}
                metricCount={metricsConfig.length}
            />
        </ErrorBoundary>
    );
}

const memoIssueMetrics = memo(IssueMetrics);
export { memoIssueMetrics as IssueMetrics };
